/**
 * Founders Page - Meet Rae & Clint
 * Luxury Design System - Range Rover Inspired
 */
import { Link } from 'react-router-dom';
import { ArrowRight, Trophy, Building, Users, Heart, ExternalLink } from 'lucide-react';

export default function FoundersPage() {
  // User-provided founder images
  const founderImages = {
    raeBlackWhite: 'https://customer-assets.emergentagent.com/job_169912b9-09da-4c66-9ed7-f663c1e945eb/artifacts/cme3inmy_image.png',
    clintGym: 'https://customer-assets.emergentagent.com/job_169912b9-09da-4c66-9ed7-f663c1e945eb/artifacts/vrucwbkt_image.png',
    bothEvent: 'https://customer-assets.emergentagent.com/job_169912b9-09da-4c66-9ed7-f663c1e945eb/artifacts/qkmfnrmv_about%20founders%20header%20image.png',
  };

  return (
    <div className="bg-white" data-testid="founders-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src={founderImages.bothEvent}
            alt="Rae Edwards and Clint Fuqua"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        
        <div className="relative z-10 container mx-auto px-6 lg:px-12 pt-32">
          <div className="max-w-3xl">
            <span className="text-[10px] font-heading tracking-[0.3em] text-white/70 mb-6 block">OUR STORY</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-light text-white tracking-[0.08em] uppercase mb-6">
              Meet the Founders
            </h1>
            <p className="text-white/70 max-w-xl text-sm tracking-wide leading-relaxed">
              Two visionaries united by a passion for transforming lives through wellness
            </p>
          </div>
        </div>
      </section>

      {/* Back to About Link */}
      <section className="py-6 border-b border-neutral-200">
        <div className="container mx-auto px-6 lg:px-12">
          <Link 
            to="/about" 
            className="inline-flex items-center gap-2 text-[10px] font-heading tracking-[0.2em] text-neutral-500 hover:text-neutral-900 transition-colors"
          >
            ← BACK TO ABOUT
          </Link>
        </div>
      </section>

      {/* Rae Section */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="lg:sticky lg:top-32">
              <div className="aspect-[3/4] overflow-hidden">
                <img 
                  src={founderImages.raeBlackWhite}
                  alt="Monzavous Rae Edwards"
                  className="w-full h-full object-cover grayscale"
                />
              </div>
            </div>
            <div>
              <span className="text-[10px] font-heading tracking-[0.3em] text-neutral-500 mb-6 block">CO-FOUNDER</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-light text-neutral-900 tracking-[0.08em] uppercase mb-2">
                Monzavous "Rae" Edwards
              </h2>
              <p className="text-sm text-neutral-500 tracking-wider uppercase mb-8">World-Class Sprint Athlete</p>
              
              <div className="space-y-6 text-neutral-600 tracking-wide leading-relaxed">
                <p>
                  Monzavous "Rae" Edwards is a world-class sprinter whose career has taken him from junior college tracks in the United States to the global stage of World Championships, Pan American Games, and the Olympic Games.
                </p>
                <p>
                  Born in 1991 in Opelika, Alabama, he first broke through in 2000 by winning both the USA Junior and Junior College Championships in the 100 and 200 meters, then reaching the semifinals and quarterfinals of those events at the World Junior Championships. These early results marked him as one of the fastest young sprinters in the country.
                </p>
                <p>
                  Over the next decade, Edwards steadily honed his craft and cemented his place among the world's top sprinters. He reached the final of the USA Outdoor Championships in 2003, running 10.09 seconds for 100 meters, and returned in 2006 to match that mark and finish sixth in the national finals.
                </p>
                <p>
                  In 2008 and 2009, he made another leap forward, improving his 200-meter best to 20.17 seconds in Brazil and his 100 meter best to 10.02 seconds in Hengelo before earning qualification to his first senior World Championships.
                </p>
                <p className="italic border-l-2 border-neutral-900 pl-6">
                  His personal bests—10.00 seconds in the 100 meters and 20.17 seconds in the 200 meters—stand as evidence of years of disciplined training, persistence through setbacks, and the ability to compete at the very highest level of global sprinting.
                </p>
              </div>

              <div className="mt-12 flex gap-12">
                <div>
                  <div className="text-3xl font-heading font-light text-neutral-900 tracking-wider">10.00s</div>
                  <div className="text-[10px] text-neutral-500 tracking-[0.15em] mt-1">100M PERSONAL BEST</div>
                </div>
                <div>
                  <div className="text-3xl font-heading font-light text-neutral-900 tracking-wider">20.17s</div>
                  <div className="text-[10px] text-neutral-500 tracking-[0.15em] mt-1">200M PERSONAL BEST</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clint Section */}
      <section className="py-24 lg:py-32 bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="order-2 lg:order-1">
              <span className="text-[10px] font-heading tracking-[0.3em] text-neutral-500 mb-6 block">CO-FOUNDER</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-light text-neutral-900 tracking-[0.08em] uppercase mb-2">
                Clint Fuqua
              </h2>
              <p className="text-sm text-neutral-500 tracking-wider uppercase mb-8">"The Health Engineer"</p>
              
              <div className="space-y-6 text-neutral-600 tracking-wide leading-relaxed">
                <p>
                  Clint Fuqua, widely recognized as "The Health Engineer," is a highly sought-after certified health coach and personal trainer serving individuals, families, and companies throughout North Dallas. With more than 20–25 years in the health and fitness industry, he combines real-world training experience with deep knowledge of exercise science, nutrition, and wellness program design.
                </p>
                <p>
                  His work ranges from one-on-one training and small-group coaching to building corporate wellness initiatives that help busy professionals stay strong, focused, and productive.
                </p>
                <p>
                  Clint's philosophy is built on personalization and sustainability: he designs programs that fit a person's actual life, not just an ideal schedule on paper. He is known for his ability to translate complex topics like biomechanics, metabolic health, and supplementation into clear, practical action steps that anyone can follow.
                </p>
                <p>
                  That same approach shows up in his writing, workshops, and media work, where he focuses on long-term habit change, prevention over prescription, and helping people stay independently healthy in an ever-changing world.
                </p>
                <p className="italic border-l-2 border-neutral-900 pl-6">
                  Whether he is speaking on stage, coaching a corporate team, or working one-on-one, Clint's mission is simple: engineer better health so people can live, work, and lead at their best for years to come.
                </p>
              </div>

              <div className="mt-12 flex flex-wrap gap-4">
                <a 
                  href="#" 
                  className="inline-flex items-center gap-2 px-6 py-3 border border-neutral-300 text-[10px] font-heading tracking-[0.2em] text-neutral-700 hover:border-black hover:text-black transition-all"
                >
                  WEBSITE <ExternalLink className="w-3 h-3" />
                </a>
                <a 
                  href="#" 
                  className="inline-flex items-center gap-2 px-6 py-3 border border-neutral-300 text-[10px] font-heading tracking-[0.2em] text-neutral-700 hover:border-black hover:text-black transition-all"
                >
                  FACEBOOK <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
            <div className="order-1 lg:order-2 lg:sticky lg:top-32">
              <div className="aspect-[3/4] overflow-hidden">
                <img 
                  src={founderImages.clintGym}
                  alt="Clint Fuqua"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Better Together Section */}
      <section className="py-24 lg:py-32 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[10px] font-heading tracking-[0.3em] text-neutral-500 mb-6 block">UNITED VISION</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-light text-white tracking-[0.08em] uppercase mb-8">
                Better Together
              </h2>
              <div className="space-y-6 text-neutral-400 tracking-wide leading-relaxed">
                <p>
                  When Clint's systematic approach to health engineering meets Rae's world-class athletic discipline, the result is Olympic Fit Wellness—a place where science meets performance, and everyone has access to elite-level guidance.
                </p>
                <p>
                  Together, they've created a wellness experience that combines Clint's expertise in sustainable health transformation with Rae's firsthand knowledge of what it takes to compete at the highest levels of sport.
                </p>
              </div>
              
              <Link 
                to="/membership" 
                className="mt-10 inline-flex items-center gap-3 bg-white text-black font-heading px-10 py-4 text-xs tracking-[0.2em] hover:bg-neutral-100 transition-all duration-300"
              >
                START YOUR JOURNEY <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div>
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_169912b9-09da-4c66-9ed7-f663c1e945eb/artifacts/qgz7yuub_image.png"
                  alt="Rae and Clint - Founders"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-heading font-light text-neutral-900 tracking-[0.08em] uppercase">
              Our Combined Impact
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-1 max-w-4xl mx-auto">
            {[
              { icon: Building, value: '15+', label: 'Wellness Locations' },
              { icon: Users, value: '50K+', label: 'Members Transformed' },
              { icon: Trophy, value: '25+', label: 'Years Combined Experience' },
              { icon: Heart, value: 'Dallas', label: 'Founded in Texas' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-8 border border-neutral-200">
                <div className="w-12 h-12 mx-auto mb-6 border border-neutral-200 flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-neutral-900" />
                </div>
                <div className="text-3xl font-heading font-light text-neutral-900 tracking-wider mb-2">
                  {stat.value}
                </div>
                <div className="text-[10px] text-neutral-500 tracking-[0.15em] uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-light text-neutral-900 tracking-[0.08em] uppercase mb-6">
            Ready to Join the OFW Family?
          </h2>
          <p className="text-neutral-500 mb-10 max-w-xl mx-auto tracking-wide">
            Experience firsthand why thousands have transformed their lives with Olympic Fit Wellness.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-1">
            <Link 
              to="/membership" 
              className="bg-black text-white font-heading px-10 py-4 text-xs tracking-[0.2em] hover:bg-neutral-800 transition-all duration-300 inline-flex items-center gap-3"
            >
              VIEW MEMBERSHIP OPTIONS <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/contact" 
              className="border border-black text-black font-heading px-10 py-4 text-xs tracking-[0.2em] hover:bg-black hover:text-white transition-all duration-300"
            >
              SCHEDULE A TOUR
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
